import { FC, ReactNode, useState } from "react";
import { Button } from "../../../components/ui/button";
import { ChevronUp } from 'lucide-react';
import { cn } from "../../../lib/utils";
import { IconChevronDown, IconMoreHorizontal } from '../../components/FigmaIcons';

interface SectionCardProps {
    /** 섹션 제목 */
    title: string;
    /** 제목 왼쪽에 표시할 아이콘 (선택) */
    icon?: ReactNode;
    /** 아이템 개수 표시 (선택) */
    count?: number;
    /** 우측 액션 버튼들 */
    actions?: ReactNode;
    /** 섹션 내용 */
    children: ReactNode;
    /** 접기/펼치기 가능 여부 (기본: true) */
    collapsible?: boolean;
    /** 초기 펼침 상태 (기본: true) */
    defaultExpanded?: boolean;
    /** 커스텀 클래스 */
    className?: string;
    /** 아이템이 없을 때도 섹션을 표시할지 여부 */
    showEmpty?: boolean;
    /** 빈 상태일 때 표시할 메시지 */
    emptyMessage?: string;
}

/**
 * Figma 기반 섹션 카드
 * - 독립 카드가 아닌 연속 패널 내부 섹션 스타일
 * - border-bottom으로 구분, 내부 padding 16px
 * - 제목: 16px Bold, #4b5563
 */
export const SectionCard: FC<SectionCardProps> = ({
    title,
    icon,
    count,
    actions,
    children,
    collapsible = true,
    defaultExpanded = true,
    className,
    showEmpty = false,
    emptyMessage,
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const toggleExpand = () => {
        if (collapsible) {
            setIsExpanded(!isExpanded);
        }
    };

    return (
        <div className={cn("w-full", className)}>
            {/* Figma: 6px 높이 회색 구분선 */}
            <div className="h-1.5 bg-muted w-full" />

            {/* 섹션 본체 */}
            <div className="border-b border-border px-4 py-4">
                {/* 헤더 */}
                <div className="flex items-center justify-between">
                    {/* 왼쪽: 제목 + 토글 */}
                    <button
                        onClick={toggleExpand}
                        disabled={!collapsible}
                        className={cn(
                            "flex items-center gap-1 text-heading-sm text-[color:rgb(var(--text-section))]",
                            collapsible && "hover:opacity-70 transition-opacity"
                        )}
                    >
                        {icon && <span className="text-muted-foreground">{icon}</span>}
                        <span className="font-bold">{title}</span>
                        {count !== undefined && (
                            <span className="font-normal">({count})</span>
                        )}
                        {collapsible && (
                            <span className="ml-0.5">
                                {isExpanded ? (
                                    <ChevronUp className="w-3.5 h-3.5" />
                                ) : (
                                    <IconChevronDown className="w-3.5 h-3.5" />
                                )}
                            </span>
                        )}
                    </button>

                    {/* 오른쪽: 액션 버튼들 */}
                    {actions && (
                        <div className="flex items-center gap-1">
                            {actions}
                        </div>
                    )}
                </div>

                {/* 제목과 컨텐츠 사이 구분선 - 점선 (Figma: stroke-dasharray 2 2, #E3E6EA) */}
                <div className="-mx-4 mt-3.5 border-b border-dashed border-border" />

                {/* 바디 */}
                {isExpanded && (
                    <div className="mt-3.5 animate-in slide-in-from-top-1 duration-200">
                        {children}
                        {showEmpty && !children && emptyMessage && (
                            <p className="text-body font-medium text-muted-foreground text-center py-2">
                                {emptyMessage}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

/** Figma 스타일 액션 버튼: 36px, p-2, rounded-8, shadow */
export const SectionActionButton: FC<{
    onClick: () => void;
    icon?: ReactNode;
    label: string;
    variant?: "default" | "ghost" | "link";
}> = ({ onClick, icon, label, variant = "ghost" }) => {
    return (
        <Button
            variant={variant === "ghost" ? "icon-panel" : variant}
            onClick={onClick}
            title={label}
        >
            {icon || <IconMoreHorizontal />}
        </Button>
    );
};

/** 리스트 아이템 래퍼 - 일관된 간격과 호버 효과 */
export const SectionListItem: FC<{
    children: ReactNode;
    onClick?: () => void;
    className?: string;
}> = ({ children, onClick, className }) => {
    return (
        <div
            onClick={onClick}
            className={cn(
                "flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg transition-colors",
                onClick && "cursor-pointer hover:bg-accent",
                className
            )}
        >
            {children}
        </div>
    );
};

export default SectionCard;
